import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Col, Flex, Form, Input, Row, Select, Skeleton, Tooltip, Typography, notification } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { generateValidationSchema } from './validation'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { ErrorMessage } from '@hookform/error-message'
import SelectCpn from './SelectCpn'
import { postAddACHExceptionRule } from 'src/app/shared/services/ACHExceptionRuleService'
import { creaditOrDebitOptions, ruleTypeOptions } from './constant'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import ConfirmModal from './ConfirmModal'
import CommonAmount from './CommonAmount'
import SelectWithSearch from './SelectWithSearch'
import { convertAllSecCode, onlyAlphaAndNumeric } from 'src/app/shared/utils'
import { debounce } from 'lodash'
import './index.scss'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { getLookupValues } from 'src/app/store/masterSetup'
import { useSelector } from 'react-redux'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'
import { getAccountList, getClientListName } from 'src/app/shared/services/ClientSetupService'

const binding: any = {
  ClientName: 'clientNames',
  AccountName: 'accountNames',
  RuleType: 'ruleType',
  CreditDebit: 'creditOrDebit',
  AmountTo: 'amountTo',
  AmountFrom: 'amountFrom',
  Description: 'ruleDescription',
  ACHOriginatorID: 'achOriginatorId',
}
const AddACHExceptionRule = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [clientList, setClientList] = useState<any[]>([])
  const [accountList, setAccountList] = useState<any[]>([])
  const [accountLoading, setAccountLoading] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingMore, setLoadingMore] = useState<boolean>(false)
  const [isFetched, setIsFetched] = useState(true)
  const [secCode, setSecCode] = useState([])
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false)
  const [duplicateRule, setDuplicateRule] = useState<string>('')
  const { mode } = useWindowSize()
  const lookupValues = useSelector(getLookupValues)
  const filterOption = (input: string, option?: any) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const defaultValues = {
    clientNames: null,
    accountNames: [],
    allAccounts: true,
    ruleDescription: '',
    ruleType: null,
    achOriginatorId: null,
    secCode: [],
    creditOrDebit: null,
    amountFrom: null,
    amountTo: null,
  }

  const {
    control,
    formState: { errors, touchedFields, dirtyFields },
    handleSubmit,
    getValues,
    clearErrors,
    trigger,
    setValue,
    setError,
  } = useForm<any>({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(generateValidationSchema(t)),
  })

  const isSaveButtonDisabled =
    !Object.keys(touchedFields)?.length ||
    !Object.keys(dirtyFields)?.length ||
    !!Object.keys(errors)?.length ||
    !getValues('clientNames') ||
    (!getValues('accountNames')?.length && !getValues('allAccounts')) ||
    getValues('ruleType') === null ||
    getValues('creditOrDebit') === null

  const handleCancel = () => {
    navigate(menuUrls.achExceptionRules)
  }

  const handleClose = () => {
    setIsOpenModal(false)
  }

  const onSubmit = async (data: any, isAddMore: boolean) => {
    const payload = {
      clientIds: data.clientNames ? [data.clientNames] : [],
      accountIds: data.accountNames?.map((item: any) => (item instanceof Object ? item.value : item)) || [],
      allAccount: data.allAccounts,
      description: data.ruleDescription,
      ruleType: Number(data.ruleType),
      achOriginatorID: !!data.achOriginatorId ? data.achOriginatorId : null,
      secCodeIds: data.secCode?.map((item: any) => (item instanceof Object ? item.value : item)) || [],
      creditDebit: Number(data.creditOrDebit),
      amountFrom: data?.amountFrom ? Number(data.amountFrom) : null,
      amountTo: data?.amountTo ? Number(data.amountTo) : null,
    }

    isAddMore ? setLoadingMore(true) : setLoading(true)
    postAddACHExceptionRule(payload)
      .then((res: any) => {
        isAddMore ? setLoadingMore(false) : setLoading(false)

        if (res?.data) {
          if (isAddMore) {
            notification.success({
              message: (
                <RichTextField
                  richText={t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACH_EXCEPTION_RULE_CREATED_SUCCESSFULLY}`, {
                    Rule_Description: data.ruleDescription,
                  })}
                />
              ),
            })
            trigger()
            setValue('ruleDescription', null)
          } else {
            navigate(menuUrls.achExceptionRules)
            notification.success({
              message: (
                <RichTextField
                  richText={t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACH_EXCEPTION_RULE_CREATED_SUCCESSFULLY}`, {
                    Rule_Description: data.ruleDescription,
                  })}
                />
              ),
            })
          }
        }
      })
      .catch((error) => {
        isAddMore ? setLoadingMore(false) : setLoading(false)
        if (error?.type === 'InlineError') {
          Object.entries(error?.errors)?.map((entry: any) => {
            if (entry[1][0] === 'PPL25') {
              setError(`${binding[entry[0]]}`, {
                message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.AT_LEAST_ONE_ACCOUNT_PER_CLIENT}`, {
                  Client_Names: clientList
                    .filter((e: any) => error?.missForClientIds.includes(e?.value))
                    .map((item: any) => item.label)
                    ?.join(','),
                }),
              })
            } else if (entry[1][0] === 'PPA05' && entry[0] === 'ACHOriginatorID') {
              setError(`${binding[entry[0]]}`, {
                message: t(`SystemMessages:PPA05`, { Object: t('ACHExceptionRule:achOriginatorId.title') }),
              })
            } else setError(`${binding[entry[0]]}`, { message: t(`SystemMessages:${entry?.[1]?.[0]}`) })
          })
        } else if (error?.type === 'ToastWarning') {
          setIsOpenModal(true)

          Object.entries(error?.errors)?.map((entry: any) => {
            setDuplicateRule(entry?.[1]?.[0])
          })
        } else {
          Object.values(error?.errors)?.map((message: any) => {
            notification.error({
              message: message[0],
            })
          })
        }
      })
  }

  const handleOnchangeClient = (e: string, clear: boolean) => {
    if (clear) {
      setValue('accountNames', null)
      clearErrors('accountNames')
    }
    setAccountLoading(true)
    getAccountList(e).then((res: any) => {
      if (res?.data) {
        setAccountList(res.data?.map((item: any) => ({ label: item?.accountName, value: item?.accountId })))
        setAccountLoading(false)
      }
    })
  }

  const debounceSearchStateFn = useCallback(debounce(handleOnchangeClient, 500), [])

  useEffect(() => {
    setIsFetched(false)

    getClientListName().then((res: any) => {
      setIsFetched(true)
      if (res?.data) {
        setClientList(
          res?.data?.map((item: any) => ({
            label: item.clientName,
            value: item.id,
          })),
        )

        if (res?.data?.length === 1) {
          setValue('clientNames', res?.data[0]?.id)
          debounceSearchStateFn(res?.data[0]?.id, true)
        }
      }
    })
    if (lookupValues && lookupValues?.SECCode?.length > 0) {
      const data = convertAllSecCode(lookupValues?.SECCode)

      setSecCode(
        data?.map((item: any) => ({
          label: item.value,
          value: item.id,
        })) as any,
      )
    }
  }, [])

  return (
    <Form name='add-exception-rule-form' className='form-container ach-exception-rules' layout='vertical'>
      {isFetched ? (
        <Flex vertical>
          <Form.Item label={t('ACHExceptionRule:client.selectClient')} className='isRequired'>
            <Controller
              name='clientNames'
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  showSearch
                  style={{ width: '100%' }}
                  maxTagCount='responsive'
                  options={clientList}
                  status={errors?.clientNames ? 'error' : ''}
                  placeholder={t('ACHExceptionRule:client.placeholder')}
                  maxTagPlaceholder={(omittedValues) => {
                    return (
                      <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                        <span>+{omittedValues?.length}</span>
                      </Tooltip>
                    )
                  }}
                  onChange={(e) => {
                    field.onChange(e)
                    debounceSearchStateFn(e, true)
                  }}
                  filterOption={filterOption}
                  filterSort={(a, b) => a?.label?.localeCompare(b.label)}
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='clientNames' />
            </Typography>
          </Form.Item>

          <SelectCpn
            control={control}
            errors={errors}
            title={t('ACHExceptionRule:account.selectAccount')}
            checkboxTitle={t('ACHExceptionRule:account.allAccounts')}
            tooltipTitle={t('ACHExceptionRule:account.tooltip')}
            placeholder={t('ACHExceptionRule:account.placeholder')}
            options={accountList}
            selectName='accountNames'
            defaultSelectedValue={[]}
            checkboxName='allAccounts'
            defaultCheckboxValue={getValues('allAccounts')}
            clearErrors={clearErrors}
            trigger={trigger}
            setValue={setValue}
            onSelectBlur={() => {
              trigger('accountNames')
            }}
            loading={accountLoading}
          />

          <Row gutter={24}>
            <Col span={24} md={12}>
              <Form.Item label={t('ACHExceptionRule:ruleDescription.title')}>
                <Controller
                  name='ruleDescription'
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      status={errors?.ruleDescription ? 'error' : ''}
                      placeholder={t('ACHExceptionRule:ruleDescription.placeholder')}
                      onChange={(e) => {
                        let e1 = e
                        e1.target.value = e.target.value.trimStart()
                        field.onChange(e1)
                        trigger('ruleDescription')
                      }}
                      onBlur={(e) => {
                        let e1 = e
                        e1.target.value = e.target.value.trim()
                        field.onChange(e1)
                      }}
                      maxLength={255}
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='ruleDescription' />
                </Typography>
              </Form.Item>
            </Col>

            <Col span={24} md={12}>
              <Form.Item label={t('ACHExceptionRule:ruleType.title')} className='isRequired'>
                <Controller
                  name='ruleType'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      style={{ width: '100%' }}
                      maxTagCount='responsive'
                      options={ruleTypeOptions}
                      status={errors?.ruleType ? 'error' : ''}
                      placeholder={t('ACHExceptionRule:ruleType.placeholder')}
                      onChange={(e) => {
                        field.onChange(e)
                      }}
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='ruleType' />
                </Typography>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24} md={12}>
              <Form.Item label={t('ACHExceptionRule:achOriginatorId.title')}>
                <Controller
                  name='achOriginatorId'
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      status={errors?.achOriginatorId ? 'error' : ''}
                      placeholder={t('ACHExceptionRule:achOriginatorId.placeholder')}
                      onChange={(e) => field.onChange(onlyAlphaAndNumeric(e, 10), { shouldValidate: true })}
                      onBlur={(e) => {
                        let e1 = e
                        e1.target.value = e.target.value.trim()
                        field.onChange(e1)
                      }}
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='achOriginatorId' />
                </Typography>
              </Form.Item>
            </Col>

            <Col span={24} md={12}>
              <Form.Item label={t('ACHExceptionRule:secCode.title')}>
                <SelectWithSearch
                  options={secCode}
                  control={control}
                  disabled={false}
                  name='secCode'
                  placeholder={t('ACHExceptionRule:secCode.placeholder')}
                  defaultValue={[]}
                  clearErrors={clearErrors}
                  errors={errors}
                  trigger={trigger}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t('ACHExceptionRule:creditOrDebit.title')} className='isRequired'>
            <Controller
              name='creditOrDebit'
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  style={{ width: '100%' }}
                  maxTagCount='responsive'
                  options={creaditOrDebitOptions}
                  status={errors?.creditOrDebit ? 'error' : ''}
                  placeholder={t('ACHExceptionRule:creditOrDebit.placeholder')}
                  onChange={(e) => {
                    field.onChange(e)
                  }}
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='creditOrDebit' />
            </Typography>
          </Form.Item>

          <Row gutter={24}>
            <Col span={24} md={12}>
              <Form.Item label={t('ACHExceptionRule:amountFrom.title')}>
                <CommonAmount
                  control={control}
                  name={'amountFrom'}
                  placeholder={t('ACHExceptionRule:amountFrom.placeholder')}
                  integerLimit={14}
                  decimalLimit={2}
                  handleTrigger={() => getValues('amountTo') && trigger('amountTo')}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='amountFrom' />
                </Typography>
              </Form.Item>
            </Col>

            <Col span={24} md={12}>
              <Form.Item label={t('ACHExceptionRule:amountTo.title')}>
                <CommonAmount
                  control={control}
                  name={'amountTo'}
                  placeholder={t('ACHExceptionRule:amountTo.placeholder')}
                  integerLimit={14}
                  decimalLimit={2}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='amountTo' />
                </Typography>
              </Form.Item>
            </Col>
          </Row>
        </Flex>
      ) : (
        <Skeleton active />
      )}
      <Flex className='button-group' justify='flex-end' gap={24}>
        <Row gutter={24} style={{ width: ['tablet', 'mobile'].includes(mode) ? '100%' : '600px' }}>
          <Col span={24} md={8} style={{ paddingBottom: mode === 'mobile' ? 24 : 0 }}>
            <Button className='cancel-button' onClick={handleCancel} style={{ width: '100%' }}>
              {t('ACHExceptionRule:button.cancel')}
            </Button>
          </Col>
          <Col span={12} md={8}>
            <Button
              className='save-more-button'
              htmlType='button'
              disabled={isSaveButtonDisabled}
              onClick={handleSubmit((data) => onSubmit(data, true))}
              loading={loadingMore}
              style={{ width: '100%' }}
            >
              {t('ACHExceptionRule:button.saveAndAddMore')}
            </Button>
          </Col>
          <Col span={12} md={8}>
            <Button
              className='save-button'
              type='primary'
              disabled={isSaveButtonDisabled}
              htmlType='button'
              onClick={handleSubmit((data) => onSubmit(data, false))}
              loading={loading}
              style={{ width: '100%' }}
            >
              {t('ACHExceptionRule:button.saveChanges')}
            </Button>
          </Col>
        </Row>
      </Flex>
      <ConfirmModal
        isOpen={isOpenModal}
        onClose={handleClose}
        ruleDescription={duplicateRule}
        onConfirm={handleClose}
      />
    </Form>
  )
}

export default AddACHExceptionRule
